"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { AlertTriangle } from "lucide-react";

interface AIDisclaimerModalProps {
  isOpen: boolean;
  onAcknowledge: () => void;
  onClose: () => void;
}

export function AIDisclaimerModal({
  isOpen,
  onAcknowledge,
  onClose,
}: AIDisclaimerModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "bg-gradient-to-b from-white to-white via-chalk p-0 overflow-hidden overflow-y-auto",
          "fixed inset-0 w-full h-full translate-x-0 translate-y-0 rounded-none m-0",
          "sm:fixed sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:w-[500px] sm:h-[600px] sm:rounded-lg sm:m-4",
          "[&>button]:hidden",
        )}
      >
        <div className="h-full sm:h-auto px-8 py-12 sm:py-10 sm:px-8">
          <DialogHeader>
            <div className="mx-auto flex h-16 w-16 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-yellow-100">
              <AlertTriangle className="h-8 w-8 sm:h-6 sm:w-6 text-yellow-600" />
            </div>
            <DialogTitle className="text-center text-2xl sm:text-xl font-semibold mt-6 sm:mt-5">
              Important Notice
            </DialogTitle>
            <DialogDescription className="text-center mt-3 sm:mt-2 text-base sm:text-sm">
              Please read and acknowledge this legal disclaimer
            </DialogDescription>
          </DialogHeader>

          <div className="mt-8 sm:mt-7 space-y-6 sm:space-y-5 text-base sm:text-sm text-gray-600">
            <p className="font-semibold text-gray-700">
              The AI assistant provides general information only and does not
              constitute professional advice. By proceeding, you acknowledge and
              agree that:
            </p>

            <ul className="list-disc pl-6 space-y-4 sm:space-y-4">
              <li className="font-semibold">
                No attorney-client, financial advisor, or real estate agent
                relationship is created through your use of this AI assistant
              </li>
              <li className="font-semibold">
                Any information provided is for educational purposes only and
                should not be relied upon as legal, financial, or real estate
                advice
              </li>
              <li className="font-semibold">
                For specific guidance on real estate transactions, legal
                matters, or financial decisions, you should consult with
                qualified licensed professionals in your jurisdiction
              </li>
            </ul>

            <p className="text-base sm:text-sm font-semibold text-gray-500">
              Masterkey assumes no liability for decisions made based on
              information provided by the AI assistant.
            </p>
          </div>

          <DialogFooter className="mt-12 sm:mt-10 flex-col gap-3 sm:gap-2 sm:flex-row sm:justify-between">
            <Button
              variant="outline"
              onClick={onClose}
              className="h-14 sm:h-10 text-lg sm:text-base rounded-2xl sm:rounded-md"
            >
              Decline
            </Button>
            <Button
              onClick={onAcknowledge}
              className="bg-smokeyBlack hover:bg-smokeyBlack/90 h-14 sm:h-10 text-lg sm:text-base rounded-2xl sm:rounded-md"
            >
              I Understand & Accept
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
